import React, { useState, useEffect } from 'react';
import { 
  Button,
  SpaceBetween,
  Modal,
  Box, 
  Link,
  Alert
} from '@cloudscape-design/components';
import { BaseTable } from './BaseTable';
import { useLocalStorage } from '../commons/use-local-storage';
import { FullPageHeader } from '../commons';
import { StatusComponent } from './table-select-filter-components';
import { submissionsApi } from 'api/Submission';
import BatchActionModal from './GenericModal';
import client from '../../api/Client';

const RequestStatus = {
  APPROVED: 'approved',
  PENDING: 'pending',
  REJECTED: 'rejected',
  ENDED: 'ended',
  ONGOING: 'ongoing'
};

const defaultStatus = { value: '0', label: 'All Status' };

const COLUMN_DEFINITIONS = [
  {
    id: 'id',
    header: 'Request ID',
    cell: item => (
      <Link 
        href={`/requests/${item.id}`}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {item.id}
      </Link>
    ),
    
  },
  {
    id: 'username',
    header: 'Username',
    cell: item => item.username,
  },
  {
    id: 'target_account_name',
    header: 'Account Name',
    cell: item => item.target_account_name,
  },
  {
    id: 'create_datetime',
    header: 'Created',
    cell: item => new Date(item.create_datetime).toLocaleString(),
  },
  {
    id: 'start_datetime',
    header: 'Start Time',
    cell: item => new Date(item.start_datetime).toLocaleString(),
  },
  {
    id: 'end_datetime',
    header: 'End Time',
    cell: item => new Date(item.end_datetime).toLocaleString(),
  },
  // {
  //   id: 'user_email',
  //   header: 'User Email',
  //   cell: item => item.user_email,
  // },
  {
    id: 'status',
    header: 'Status',
    cell: item => <StatusComponent status={item.status} />,
  }
];

const STATUS_OPTIONS = [
  defaultStatus,
  ...Object.entries(RequestStatus).map(([key, value], index) => ({
    label: value,
    value: (index + 1).toString()
  }))
];

export function AllRequestsTable() {
  const [data, setData] = useState([]);
  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadMoreIsVisible, setLoadMoreIsVisible] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isApproveModalVisible, setApproveModalVisible] = useState(false);
  const [isRejectModalVisible, setRejectModalVisible] = useState(false);
  const [isRevokeModalVisible, setRevokeModalVisible] = useState(false);
  const [actionResults, setActionResults] = useState([]);
  const [alertVisible, setAlertVisible] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [preferences, setPreferences] = useLocalStorage('Request-Table-Preferences', {
    pageSize: 10,
    wrapLines: false,
    stripedRows: false,
    contentDensity: 'comfortable',
  });

  // Helper functions to check button states
  const canRevoke = (selectedItems) => {
    if (!selectedItems.length) return false;
    return selectedItems.every(item => item.status === RequestStatus.ONGOING);
  };

  const canApprove = (selectedItems) => {
    if (!selectedItems.length) return false;
    return selectedItems.every(item => item.status === RequestStatus.PENDING);
  };

  const canReject = (selectedItems) => {
    if (!selectedItems.length) return false;
    return selectedItems.every(item => 
      item.status === RequestStatus.PENDING || item.status === RequestStatus.APPROVED
    );
  };

  const fetchData = async (refresh) => {
    const isRefresh = refresh === true 
    try {
      const params = {
        limit: preferences.pageSize
      };
      if (!isRefresh){
        if (lastEvaluatedKey) {
          params.last_evaluated_key = lastEvaluatedKey;
        }

      }
          
      if (statusFilter.value !== '0') {
        params.status = statusFilter.label;
      }
      
      const result = await submissionsApi.getUserAccess(params);
  
      if (lastEvaluatedKey) {
        setData(prev => [...prev, ...result.items]);
      } else {
        setData(result.items);
      }
  
      setLoadMoreIsVisible(result.last_evaluated_key !== null);
      setLastEvaluatedKey(result.last_evaluated_key);
    } catch (error) {
      console.error('Error fetching data:', error);
      // You might want to add error state handling here
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetTableState = () => {
    setLastEvaluatedKey(null);
    setData([]);
    setLoadMoreIsVisible(true);
    setSelectedItems([]);
};

  useEffect(() => {
      resetTableState();
      fetchData(true);
  }, [preferences.pageSize, statusFilter]);

  useEffect(() => {
      if (refresh===true){
      resetTableState();
      fetchData(true);
      setRefresh(false);
      }
  }, [refresh]);

  useEffect(() => {
    const pollInterval = setInterval(() => {
      const itemsToUpdate = data.filter(item => 
        ['pending', 'approved'].includes(item.status?.toLowerCase())
      );

      if (itemsToUpdate.length > 0) {
        Promise.all(
          itemsToUpdate.map(item => client.get(`/api/v1/user-access/${item.id}/status`))
        ).then(updates => {
          setData(prev => prev.map(item => {
            const update = updates.find(u => u.id === item.id);
            return update && update.status !== item.status ? { ...item, status: update.status } : item;
          }));
        }).catch(error => {
          console.error('Error polling status:', error);
        });
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [data]);


  const handleAction = async (action, itemId, options = {}) => {
    const response = await submissionsApi.performUserAccessAction(action, itemId, options);
    return response;
  };


  const handleResultsChange = async (results) => {
    setActionResults(results);
    setAlertVisible(true);
    setRefresh(true);
  };

  const getAlertType = () => {
    if (!actionResults.length) return 'info';
    if (actionResults.every(r => r.success)) return 'success';
    if (actionResults.every(r => !r.success)) return 'error';
    return 'warning';
  };

  const getAlertHeader = () => {
    const successCount = actionResults.filter(r => r.success).length;
    const totalCount = actionResults.length;
    
    if (successCount === totalCount) {
      return 'All operations completed successfully';
    } else if (successCount === 0) {
      return 'All operations failed';
    } else {
      return `${successCount} of ${totalCount} operations completed successfully`;
    }
  };

  const tableHeader = (
    <FullPageHeader
      title="All Requests"
      selectedItemsCount={selectedItems.length}
      actions={
        <SpaceBetween size="xs" direction="horizontal">
          <Button 
            variant="primary" 
            onClick={() => setRevokeModalVisible(true)}
            disabled={!canRevoke(selectedItems)}
          >
            Revoke
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setApproveModalVisible(true)}
            disabled={!canApprove(selectedItems)}
          >
            Approve
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setRejectModalVisible(true)}
            disabled={!canReject(selectedItems)}
          >
            Reject
          </Button>
        </SpaceBetween>
      }
    />
  );

  return (
    <>
      {alertVisible && actionResults.length > 0 && (
      <Alert
        onDismiss={() => setAlertVisible(false)}
        type={getAlertType()}
        header={getAlertHeader()}
        dismissible
      >
        <SpaceBetween size="m">
          {actionResults.map((result, index) => (
            <div key={index}>
              {result.message}
            </div>
          ))}
        </SpaceBetween>
      </Alert>
    )}


      <BaseTable
        columnDefinitions={COLUMN_DEFINITIONS}
        items={data}
        loading={isLoading}
        statusOptions={STATUS_OPTIONS}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onFetchData={fetchData}
        preferences={preferences}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loadMoreIsVisible={loadMoreIsVisible}
        tableHeader={tableHeader}
      />

      {/* Action Modals */}
      <BatchActionModal
        visible={isApproveModalVisible}
        onDismiss={() => setApproveModalVisible(false)}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        actionType="approve"
        onAction={handleAction}
        onResultsChange={handleResultsChange}
        title="Approve Requests"
      />

      <BatchActionModal
        visible={isRejectModalVisible}
        onDismiss={() => setRejectModalVisible(false)}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        actionType="reject"
        onAction={handleAction}
        onResultsChange={handleResultsChange}
        title="Reject Requests"
      />

      <BatchActionModal
        visible={isRevokeModalVisible}
        onDismiss={() => setRevokeModalVisible(false)}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        actionType="revoke"
        onAction={handleAction}
        onResultsChange={handleResultsChange}
        title="Revoke Requests"
      />
    </>
  );
}

export default AllRequestsTable;